<template>
  <div class="sl-page">
    <!-- ═══ APPBAR ═══ -->
    <header class="appbar">
      <router-link to="/" class="vm-mark">
        <img
          class="vm-logo"
          src="../assets/images/logo-videomedi.png"
          alt="Videomedi"
        />
      </router-link>
      <nav class="appnav">
        <router-link to="/" exact active-class="is-active">Início</router-link>
        <router-link
          :to="{ path: '/', query: { buscar: '1' } }"
          active-class="is-active"
        >
          Buscar profissionais de saúde
        </router-link>
        <router-link v-if="loggedIn" to="/consultas" active-class="is-active">
          Minhas consultas
        </router-link>
        <router-link v-if="loggedIn" to="/documentos" active-class="is-active">
          Documentos
        </router-link>
        <router-link
          v-if="loggedIn"
          to="/meus-pagamentos"
          active-class="is-active"
        >
          Pagamentos
        </router-link>
      </nav>
      <span class="appbar-spacer" />
      <span class="appbar-hint">
        Já tem conta?
        <a @click="activeTab = 'login'">Entrar</a>
      </span>
    </header>

    <!-- ═══ WIZARD ═══ -->
    <div class="wizard-row">
      <div class="steps">
        <div class="step step--done">
          <span class="step-dot">✓</span>
          <span class="step-label">Profissional</span>
        </div>
        <span class="step-bar step-bar--done" />
        <template v-if="!isExpressAppointment">
          <div class="step step--done">
            <span class="step-dot">✓</span>
            <span class="step-label">Horário</span>
          </div>
          <span class="step-bar step-bar--done" />
        </template>
        <div class="step step--active">
          <span class="step-dot">{{ isExpressAppointment ? "2" : "3" }}</span>
          <span class="step-label">Pagamento</span>
        </div>
        <span class="step-bar" />
        <div class="step">
          <span class="step-dot">{{ isExpressAppointment ? "3" : "4" }}</span>
          <span class="step-label">Confirmação</span>
        </div>
      </div>
    </div>

    <!-- ═══ CHECKOUT ═══ -->
    <div class="checkout">
      <div class="checkout-grid">
        <!-- FORMULÁRIO DE AUTENTICAÇÃO -->
        <div class="card auth-card">
          <div class="tabs">
            <span
              class="tab"
              :class="{ 'tab--active': activeTab === 'login' }"
              @click="activeTab = 'login'"
            >
              Entrar
            </span>
            <span
              class="tab"
              :class="{ 'tab--active': activeTab === 'register' }"
              @click="goRegister"
            >
              Criar conta
            </span>
          </div>

          <div>
            <h2>Entre para confirmar</h2>
            <p class="sub">
              Use sua conta Videomedi para concluir o agendamento.
            </p>
          </div>

          <form class="fieldset" @submit.prevent="handleLogin">
            <div class="field">
              <label class="label" for="sl-email">E-mail</label>
              <input
                id="sl-email"
                class="input"
                type="email"
                placeholder="seu@email.com"
                v-model="form.email"
                :class="{ 'input--error': errors.email }"
                autocomplete="email"
              />
              <span v-if="errors.email" class="field-msg field-msg--error">
                {{ errors.email }}
              </span>
            </div>

            <div class="field">
              <label class="label" for="sl-password">Senha</label>
              <input
                id="sl-password"
                class="input"
                type="password"
                placeholder="Sua senha"
                v-model="form.password"
                :class="{ 'input--error': errors.password }"
                autocomplete="current-password"
              />
              <span v-if="errors.password" class="field-msg field-msg--error">
                {{ errors.password }}
              </span>
              <router-link to="/recuperarSenha" class="forgot">
                Esqueci minha senha
              </router-link>
            </div>

            <span v-if="errors.general" class="field-msg field-msg--error">
              {{ errors.general }}
            </span>

            <button
              type="submit"
              class="btn btn--accent btn--lg btn--block"
              :disabled="isLoading"
            >
              <span v-if="!isLoading">Entrar e continuar</span>
              <span v-else>Entrando…</span>
            </button>
          </form>

          <p class="register-hint">
            Não tem conta?
            <a @click="goRegister">Criar cadastro</a>
          </p>
        </div>

        <!-- RESUMO DO AGENDAMENTO -->
        <aside class="card summary-card">
          <div class="reserved">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="12"
                cy="12"
                r="9"
                stroke="currentColor"
                stroke-width="2"
              />
              <path
                d="M12 7v5l3 2"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
            <div class="stack" style="gap:0;flex:1">
              <span class="res-title">Horário reservado</span>
              <span class="res-sub">Guardamos por 10 minutos</span>
            </div>
          </div>

          <div class="summary-doc">
            <div class="avatar avatar--md avatar--ring">
              <img
                v-if="doctor.photo"
                :src="doctor.photo"
                :alt="doctorName"
                loading="lazy"
                decoding="async"
              />
              <template v-else>{{ doctorInitials }}</template>
            </div>
            <div class="stack" style="gap:1px">
              <span class="dnm">{{ doctorName }}</span>
              <span class="dsub">{{ doctorSpecialty }} · {{ doctorCRM }}</span>
            </div>
          </div>

          <div class="stack" style="gap:var(--space-3)">
            <div class="summary-row">
              <span class="k">Data</span>
              <span class="v">{{ scheduledDate }}</span>
            </div>
            <div class="summary-row">
              <span class="k">Horário</span>
              <span class="v hl">{{ scheduledTime }}</span>
            </div>
            <div class="summary-row">
              <span class="k">Modalidade</span>
              <span class="v">Vídeo · 30 min</span>
            </div>
          </div>

          <div class="summary-total">
            <span class="tl">Total</span>
            <span class="tv">{{ doctorPrice }}</span>
          </div>

          <div class="book-trust">
            <span class="t">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M20 6L9 17l-5-5"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              Cancelamento grátis até 24h antes
            </span>
            <span class="t">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M12 2l8 3v6c0 5-3.5 8.5-8 11-4.5-2.5-8-6-8-11V5l8-3z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linejoin="round"
                />
              </svg>
              Seus dados são protegidos (LGPD)
            </span>
          </div>
        </aside>
      </div>
    </div>

    <!-- ═══ RODAPÉ ═══ -->
    <footer class="foot-mini">
      <span class="vm-mark">
        <img
          class="vm-logo vm-logo--sm"
          src="../assets/images/logo-videomedi.png"
          alt="Videomedi"
        />
      </span>
      <span class="cpy">© 2026 Videomedi · CNPJ 00.000.000/0001-00</span>
      <span class="links">
        <a href="#">Privacidade</a>
        <a href="#">Termos de Uso</a>
        <a href="#">LGPD</a>
        <a href="#">Suporte</a>
      </span>
    </footer>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { intToBRL } from "./../utils/currency/convert";
import { isImmediateConsultation } from "./../utils/appointments/immediateConsultation";

export default {
  name: "ScheduleLoginView",

  data() {
    return {
      activeTab: "login",
      form: { email: "", password: "" },
      errors: {},
      isLoading: false
    };
  },

  methods: {
    ...mapActions("auth", ["login"]),

    async handleLogin() {
      this.errors = {};
      if (!this.form.email) {
        this.errors.email = "Informe seu e-mail";
        return;
      }
      if (!this.form.password) {
        this.errors.password = "Informe sua senha";
        return;
      }

      this.isLoading = true;
      try {
        const path = await this.login({
          email: this.form.email,
          password: this.form.password,
          redirectFrom: "/agendamento"
        });
        this.$router.push({ path: path || "/agendamento" });
      } catch {
        this.errors.general = "E-mail ou senha incorretos. Tente novamente.";
      } finally {
        this.isLoading = false;
      }
    },

    goRegister() {
      this.$router.push({
        name: "Register",
        query: { redirectFrom: "/agendamento" }
      });
    }
  },

  computed: {
    ...mapGetters("auth", ["loggedIn"]),
    ...mapGetters("appointments", ["currentSchedule"]),

    isExpressAppointment() {
      return isImmediateConsultation(this.currentSchedule);
    },
    schedule() {
      return this.currentSchedule || {};
    },
    doctor() {
      return this.schedule.doctor || {};
    },
    doctorInitials() {
      return (
        (this.doctor.name || "")
          .split(" ")
          .slice(0, 2)
          .map(n => n[0])
          .join("")
          .toUpperCase() || ""
      );
    },
    doctorName() {
      return this.doctor.name || "—";
    },
    doctorSpecialty() {
      return this.doctor.doctor_property?.medical_specialty_name || "—";
    },
    doctorCRM() {
      return this.doctor.doctor_property?.council_description || "—";
    },
    doctorPrice() {
      const price = this.doctor.doctor_property
        ?.price_cents_and_earning_margin_sum;
      return price ? intToBRL(price) : "—";
    },
    scheduledDate() {
      const dt = this.schedule.scheduled_at;
      if (!dt) return "Qui, 11 jun";
      const d = new Date(dt);
      return d
        .toLocaleDateString("pt-BR", {
          weekday: "short",
          day: "numeric",
          month: "short"
        })
        .replace(/\.\s*/g, " ")
        .trim()
        .replace(/^\w/, c => c.toUpperCase());
    },
    scheduledTime() {
      const dt = this.schedule.scheduled_at;
      if (!dt) return "08:00";
      return new Date(dt).toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit"
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.vm-logo {
  display: block;
  height: 28px;
  width: auto;
}

.vm-logo--sm {
  height: 22px;
}

/* ─── PAGE ─── */
.sl-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--color-bg-page, #f4f6fb);
}

/* ─── APPBAR ─── */
.appbar {
  display: flex;
  align-items: center;
  gap: var(--space-8);
  background: #fff;
  border-bottom: 1px solid var(--color-gray-200);
  padding: 0 var(--space-8);
  height: 68px;
  position: sticky;
  top: 0;
  z-index: 10;
}

.vm-mark {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  text-decoration: none;
}

.vm-wordmark {
  font-size: var(--font-size-md);
  font-weight: var(--fw-extra);
  color: var(--color-accent);
  letter-spacing: -0.01em;
}

.appnav {
  display: flex;
  align-items: center;
  gap: var(--space-6);

  a {
    font-size: var(--font-size-sm);
    font-weight: var(--fw-semibold);
    color: var(--color-gray-600);
    text-decoration: none;
    cursor: pointer;

    &:hover {
      color: var(--color-primary);
    }
    &.is-active {
      color: var(--color-accent);
    }
    &.router-link-exact-active {
      color: var(--color-primary);
    }
  }
}

.appbar-spacer {
  flex: 1;
}

.appbar-hint {
  font-size: var(--font-size-xs);
  color: var(--color-gray-400);
  font-weight: var(--fw-semibold);

  a {
    color: var(--color-primary);
    font-weight: var(--fw-bold);
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
}

/* ─── WIZARD ─── */
.wizard-row {
  display: flex;
  justify-content: center;
  padding: var(--space-6) var(--space-10) 0;
}

.steps {
  display: flex;
  align-items: center;
  max-width: 620px;
  width: 100%;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);

  .step-dot {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 2px solid var(--color-gray-300);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--font-size-xs);
    font-weight: var(--fw-bold);
    color: var(--color-gray-400);
    background: #fff;
  }

  .step-label {
    font-size: var(--font-size-xs);
    font-weight: var(--fw-semibold);
    color: var(--color-gray-400);
    white-space: nowrap;
  }

  &.step--done .step-dot {
    background: var(--color-success);
    border-color: var(--color-success);
    color: #fff;
  }
  &.step--done .step-label {
    color: var(--color-gray-700);
  }
  &.step--active .step-dot {
    background: var(--color-primary);
    border-color: var(--color-primary);
    color: #fff;
  }
  &.step--active .step-label {
    color: var(--color-primary);
    font-weight: var(--fw-bold);
  }
}

.step-bar {
  flex: 1;
  height: 2px;
  background: var(--color-gray-200);
  margin: 0 var(--space-1);
  margin-top: -16px;

  &.step-bar--done {
    background: var(--color-success);
  }
}

/* ─── CHECKOUT ─── */
.checkout {
  max-width: 960px;
  margin: 0 auto;
  padding: var(--space-8) var(--space-10) var(--space-12);
  width: 100%;

  @media (max-width: 860px) {
    padding: var(--space-5);
  }
}

.checkout-grid {
  display: grid;
  grid-template-columns: 1fr 348px;
  gap: var(--space-8);
  align-items: start;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
}

/* ─── CARD: sem padding global no app ─── */
.card {
  padding: var(--space-6);
}

/* ─── AUTH CARD ─── */
.auth-card {
  padding: var(--space-8);
  display: flex;
  flex-direction: column;
  gap: var(--space-5);

  h2 {
    font-size: var(--font-size-xl);
    font-weight: var(--fw-bold);
    color: var(--color-gray-900);
    margin: 0 0 var(--space-1);
  }

  .sub {
    font-size: var(--font-size-sm);
    color: var(--color-gray-500);
    margin: 0;
  }
}

/* ─── TABS ─── */
.tabs {
  display: flex;
  gap: var(--space-6);
  border-bottom: 1.5px solid var(--color-gray-200);
}

.tab {
  font-size: var(--font-size-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-gray-500);
  padding-bottom: var(--space-3);
  border-bottom: 2px solid transparent;
  margin-bottom: -1.5px;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.12s, border-color 0.12s;

  &:hover {
    color: var(--color-primary);
  }
  &.tab--active {
    color: var(--color-primary);
    border-bottom-color: var(--color-primary);
  }
}

/* ─── FIELDSET / FIELD ─── */
.fieldset {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.label {
  font-size: var(--font-size-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-gray-700);
}

.input {
  border: 1.5px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  height: 48px;
  padding: 0 var(--space-4);
  font-size: var(--font-size-md);
  color: var(--color-gray-900);
  background: #fff;
  width: 100%;
  outline: none;
  transition: border-color 0.12s;
  box-sizing: border-box;

  &::placeholder {
    color: var(--color-gray-400);
  }
  &:focus {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px var(--color-primary-light);
  }
  &.input--error {
    border-color: var(--color-danger);
  }
}

.field-msg {
  font-size: var(--font-size-xs);
  display: flex;
  align-items: center;
  gap: 4px;
  &.field-msg--error {
    color: var(--color-danger);
  }
}

.forgot {
  font-size: var(--font-size-xs);
  font-weight: var(--fw-bold);
  color: var(--color-primary);
  align-self: flex-end;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
}

.register-hint {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  text-align: center;
  margin: 0;

  a {
    color: var(--color-accent);
    font-weight: var(--fw-bold);
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
}

/* ─── SUMMARY CARD ─── */
.summary-card {
  position: sticky;
  top: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  border-color: var(--color-primary) !important;

  @media (max-width: 860px) {
    position: static;
  }
}

/* ─── RESERVED ─── */
.reserved {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  background: var(--color-success-light, #dcfce7);
  border-radius: var(--radius-md);
  padding: var(--space-3) var(--space-4);

  svg {
    color: var(--color-success);
    flex: none;
  }

  .res-title {
    display: block;
    font-size: var(--font-size-sm);
    font-weight: var(--fw-bold);
    color: var(--color-success);
  }

  .res-sub {
    display: block;
    font-size: var(--font-size-xs);
    color: var(--color-gray-600);
  }
}

/* ─── SUMMARY DOC ─── */
.summary-doc {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding-bottom: var(--space-4);
  border-bottom: 1px solid var(--color-gray-100);

  .dnm {
    display: block;
    font-weight: var(--fw-bold);
    color: var(--color-gray-900);
    font-size: var(--font-size-sm);
  }

  .dsub {
    display: block;
    font-size: var(--font-size-xs);
    color: var(--color-gray-500);
  }
}

/* ─── SUMMARY ROWS ─── */
.summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: var(--font-size-sm);

  .k {
    color: var(--color-gray-500);
  }
  .v {
    font-weight: var(--fw-bold);
    color: var(--color-gray-900);
  }
  .v.hl {
    color: var(--color-success);
  }
}

.summary-total {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-gray-200);

  .tl {
    font-size: var(--font-size-sm);
    color: var(--color-gray-500);
    font-weight: var(--fw-semibold);
  }
  .tv {
    font-family: var(--font-num, "Inter", sans-serif);
    font-size: var(--font-size-2xl);
    font-weight: var(--fw-bold);
    color: var(--color-gray-900);
  }
}

/* ─── BOOK TRUST ─── */
.book-trust {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding-top: var(--space-2);
  border-top: 1px solid var(--color-gray-100);

  .t {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--font-size-xs);
    color: var(--color-gray-500);

    svg {
      color: var(--color-success);
      flex: none;
    }
  }
}

/* ─── FOOT MINI ─── */
.foot-mini {
  display: flex;
  align-items: center;
  gap: var(--space-5);
  padding: var(--space-5) var(--space-8);
  border-top: 1px solid var(--color-gray-200);
  background: var(--color-bg-page, #f4f6fb);
  margin-top: auto;

  .vm-mark {
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
  }
  .vm-wordmark {
    color: var(--color-gray-500);
    font-size: var(--font-size-sm);
    font-weight: normal;
  }
  .cpy {
    font-size: var(--font-size-xs);
    color: var(--color-gray-400);
  }
  .links {
    margin-left: auto;
    display: flex;
    gap: var(--space-5);

    a {
      font-size: var(--font-size-xs);
      font-weight: var(--fw-semibold);
      color: var(--color-gray-500);
      text-decoration: none;
      &:hover {
        color: var(--color-primary);
      }
    }
  }
}
</style>
